// src/components/Dashboard/types.ts
// Shared types, catalogue, and preference helpers for the widget system.

export type WidgetId =
  | 'active-jobs'
  | 'tender-pipeline'
  | 'spend-budget'
  | 'crew-onsite'
  | 'open-pos'
  | 'progress-claims'
  | 'safety-alerts'
  | 'milestones'
  | 'itp-hold-points'
  | 'plant-status'
  | 'subcontractors'
  | 'compliance-expiry'
  | 'timesheets'
  | 'rfq-activity'
  | 'xero-status'
  | 'archive-filings'
  | 'induction-expiry'
  | 'travel-today'

export interface WidgetConfig {
  id:        WidgetId
  label:     string
  defaultOn: boolean
  /** Minimum platform tier required to see this widget. */
  minTier:   string
  /** 12-column grid column span. */
  colSpan:   3 | 4 | 6 | 12
}

/** Props injected into every widget component. */
export interface WidgetProps {
  onNavigate:   (page: string) => void
  scopedJobIds: string[] | null
}

// ── Tier hierarchy (index 0 = highest access) ──────────────────────────────
export const TIER_ORDER = [
  'A_DEVELOPER',
  'B_PM',
  'B_PC',
  'C_PROCUREMENT',
  'D_FIELD',
]

export function canAccessTier(userTier: string, minTier: string): boolean {
  const u = TIER_ORDER.indexOf(userTier)
  const m = TIER_ORDER.indexOf(minTier)
  return u !== -1 && m !== -1 && u <= m
}

// ── Widget catalogue — 18 widgets per spec ─────────────────────────────────
export const WIDGET_CATALOGUE: WidgetConfig[] = [
  { id: 'active-jobs',       label: 'Active Jobs',                    defaultOn: true,  minTier: 'D_FIELD',       colSpan: 6  },
  { id: 'tender-pipeline',   label: 'Tender Pipeline',                defaultOn: true,  minTier: 'B_PC',          colSpan: 6  },
  { id: 'spend-budget',      label: 'Spend vs Budget',                defaultOn: true,  minTier: 'B_PC',          colSpan: 6  },
  { id: 'crew-onsite',       label: 'Crew On-Site Today',             defaultOn: true,  minTier: 'B_PC',          colSpan: 6  },
  { id: 'open-pos',          label: 'Open Purchase Orders',           defaultOn: true,  minTier: 'C_PROCUREMENT', colSpan: 4  },
  { id: 'progress-claims',   label: 'Progress Claims Status',         defaultOn: true,  minTier: 'B_PC',          colSpan: 4  },
  { id: 'safety-alerts',     label: 'Safety Alerts and Incidents',    defaultOn: true,  minTier: 'D_FIELD',       colSpan: 4  },
  { id: 'milestones',        label: 'Upcoming Milestones',            defaultOn: false, minTier: 'B_PC',          colSpan: 6  },
  { id: 'itp-hold-points',   label: 'ITP Hold Points Pending',        defaultOn: false, minTier: 'B_PC',          colSpan: 6  },
  { id: 'plant-status',      label: 'Plant and Equipment Status',     defaultOn: false, minTier: 'B_PC',          colSpan: 4  },
  { id: 'subcontractors',    label: 'Subcontractor Activity',         defaultOn: false, minTier: 'B_PC',          colSpan: 6  },
  { id: 'compliance-expiry', label: 'Rego and Compliance Expiry',     defaultOn: false, minTier: 'B_PC',          colSpan: 6  },
  { id: 'timesheets',        label: 'Recent Timesheets',              defaultOn: false, minTier: 'C_PROCUREMENT', colSpan: 4  },
  { id: 'rfq-activity',      label: 'RFQ Awaiting Response',          defaultOn: false, minTier: 'C_PROCUREMENT', colSpan: 4  },
  { id: 'xero-status',       label: 'Xero Reconciliation Status',     defaultOn: false, minTier: 'A_DEVELOPER',   colSpan: 4  },
  { id: 'archive-filings',   label: 'Archive Recent Filings',         defaultOn: false, minTier: 'B_PC',          colSpan: 6  },
  { id: 'induction-expiry',  label: 'Induction Expiry Alerts',        defaultOn: false, minTier: 'D_FIELD',       colSpan: 4  },
  { id: 'travel-today',      label: 'Travel and Accommodation Today', defaultOn: false, minTier: 'D_FIELD',       colSpan: 4  },
]

// ── Preference persistence — localStorage until Supabase connects ──────────
const PREFS_KEY = 'tp_widget_prefs'

export function loadWidgetPrefs(userId: string): Partial<Record<WidgetId, boolean>> {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    if (!raw) return {}
    return (JSON.parse(raw) as Record<string, Partial<Record<WidgetId, boolean>>>)[userId] ?? {}
  } catch {
    return {}
  }
}

export function saveWidgetPrefs(
  userId: string,
  prefs:  Partial<Record<WidgetId, boolean>>,
): void {
  try {
    const raw = localStorage.getItem(PREFS_KEY)
    const all = raw
      ? (JSON.parse(raw) as Record<string, Partial<Record<WidgetId, boolean>>>)
      : {}
    all[userId] = prefs
    localStorage.setItem(PREFS_KEY, JSON.stringify(all))
  } catch {
    // silently ignore storage failures
  }
}

/** Returns true if the widget should be visible for this user. */
export function isWidgetVisible(
  widgetId:   WidgetId,
  userTier:   string,
  savedPrefs: Partial<Record<WidgetId, boolean>>,
): boolean {
  const cfg = WIDGET_CATALOGUE.find(w => w.id === widgetId)
  if (!cfg) return false
  if (!canAccessTier(userTier, cfg.minTier)) return false
  return widgetId in savedPrefs ? (savedPrefs[widgetId] as boolean) : cfg.defaultOn
}

/** Returns the ordered list of WidgetConfig entries that should be rendered. */
export function getActiveWidgets(
  userTier:   string,
  savedPrefs: Partial<Record<WidgetId, boolean>>,
): WidgetConfig[] {
  return WIDGET_CATALOGUE.filter(cfg =>
    canAccessTier(userTier, cfg.minTier) &&
    isWidgetVisible(cfg.id, userTier, savedPrefs),
  )
}
